// useWatchlists — v2 hook backed by Supabase via /api/watchlist.
//
// Replaces the previous localStorage-only implementation. Both /watchlist
// and the Today page's WatchlistsPanel + HeroSection now consume the same
// account-tied state — no more divergence between pages.
//
// The hook surface is intentionally identical to the prior version so
// existing consumers (panels.jsx, HeroSection.jsx) keep working unchanged.
// Mutations are optimistic; on error, the next API refresh corrects the
// local state.
//
// SignedOut state (no Clerk user_id yet) renders an empty list — there is
// nothing to show before auth resolves, and the page sits behind a hard
// auth wall anyway.

import { useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import {
  getUserLists,
  getUserPref,
  setUserPref,
  createList,
  renameList,
  deleteList,
  addStockToList,
  removeStockFromList,
} from '@/lib/watchlistClient';

const ACTIVE_PREF_KEY = 'watchlist.active';

export function useWatchlists() {
  const { user, isLoaded } = useUser();
  const userId = user?.id || null;

  const [watchlists, setWatchlists] = useState([]);
  const [active, setActiveState] = useState('all');
  // `loading` starts true so consumers don't flash the empty-state during
  // the initial fetch. Flips false once a refresh resolves (or fails).
  // Subsequent refreshes also briefly flip it true; the page treats
  // first-load (no data yet) differently from a stale-while-revalidating
  // refresh by checking watchlists.length alongside loading.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async (uid) => {
    if (!uid) {
      setWatchlists([]);
      setActiveState('all');
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const [listsRes, activeRes] = await Promise.all([
        getUserLists(uid),
        getUserPref(uid, ACTIVE_PREF_KEY),
      ]);
      setWatchlists((listsRes.lists || []).map(l => ({
        id: l.id,
        name: l.name,
        tickers: l.tickers || [],
        entries: l.entries || {}, // { [ticker]: { entry_price, added_at, entry_at } }
      })));
      const stored = typeof activeRes.value === 'string' ? activeRes.value : 'all';
      setActiveState(stored);
      setError(null);
    } catch (e) {
      console.error('[useWatchlists] refresh failed', e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    refresh(userId);
  }, [isLoaded, userId, refresh]);

  const setActive = useCallback((id) => {
    setActiveState(id);
    if (userId) setUserPref(userId, ACTIVE_PREF_KEY, id).catch(e => console.error('[useWatchlists] setActive', e));
  }, [userId]);

  const create = useCallback((name) => {
    if (!userId) return null;
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    // Optimistic: temp id replaced on refresh
    const tempId = `tmp_${Date.now()}`;
    setWatchlists(prev => [...prev, { id: tempId, name: trimmed, tickers: [], entries: {} }]);
    createList(userId, trimmed)
      .then(({ list }) => {
        setActiveState(list.id);
        setUserPref(userId, ACTIVE_PREF_KEY, list.id).catch(() => {});
        refresh(userId);
      })
      .catch(e => { console.error('[useWatchlists] create', e); refresh(userId); });
    return tempId;
  }, [userId, refresh]);

  const rename = useCallback((id, name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    setWatchlists(prev => prev.map(w => w.id === id ? { ...w, name: trimmed } : w));
    renameList(id, trimmed).catch(e => { console.error('[useWatchlists] rename', e); refresh(userId); });
  }, [userId, refresh]);

  const remove = useCallback((id) => {
    setWatchlists(prev => prev.filter(w => w.id !== id));
    setActiveState(cur => (cur === id ? 'all' : cur));
    deleteList(id).catch(e => { console.error('[useWatchlists] remove', e); refresh(userId); });
  }, [userId, refresh]);

  const addTicker = useCallback((id, ticker) => {
    const tk = String(ticker || '').toUpperCase();
    if (!tk) return;
    setWatchlists(prev => prev.map(w =>
      w.id === id && !w.tickers.includes(tk)
        ? { ...w, tickers: [...w.tickers, tk] }
        : w));
    addStockToList(id, tk).catch(e => { console.error('[useWatchlists] addTicker', e); refresh(userId); });
  }, [userId, refresh]);

  const removeTicker = useCallback((id, ticker) => {
    const tk = String(ticker || '').toUpperCase();
    setWatchlists(prev => prev.map(w =>
      w.id === id ? { ...w, tickers: w.tickers.filter(t => t !== tk) } : w));
    removeStockFromList(id, tk).catch(e => { console.error('[useWatchlists] removeTicker', e); refresh(userId); });
  }, [userId, refresh]);

  const activeWatchlist = watchlists.find(w => w.id === active) ?? null;

  return {
    watchlists,
    active,
    setActive,
    activeWatchlist,
    create,
    rename,
    remove,
    addTicker,
    removeTicker,
    loading,
    error,
    refresh: () => refresh(userId),
  };
}
